import React from 'react';

import { MenuItem, Stack, TextField, Typography } from '@mui/material';

import { EGS_BEDDING_OPTIONS, EGS_RATE_TYPE_OPTIONS } from './fields';
import type { EgsCategoryMeta, EgsGuestCategoryDraft } from './types';

interface Props {
  category: EgsGuestCategoryDraft;
  currency: string;
  enabled: boolean;
  meta: EgsCategoryMeta;
  onChange: (changes: Partial<EgsGuestCategoryDraft>) => void;
}

export default function EgsCategoryFieldsForm({ category, currency, enabled, meta, onChange }: Props) {
  return (
    <>
      <Stack direction="row" spacing={1}>
        {(meta.ages === 'from' || meta.ages === 'both') && (
          <TextField
            InputLabelProps={{ shrink: true }}
            disabled={!enabled}
            label="Aged From"
            onChange={(event) => onChange({ fromAge: event.target.value })}
            required
            size="small"
            type="number"
            value={category.fromAge}
          />
        )}
        {(meta.ages === 'to' || meta.ages === 'both') && (
          <TextField
            InputLabelProps={{ shrink: true }}
            disabled={!enabled}
            label="Aged To"
            onChange={(event) => onChange({ toAge: event.target.value })}
            required
            size="small"
            type="number"
            value={category.toAge}
          />
        )}
      </Stack>

      <TextField
        disabled={!enabled}
        label="Inclusions"
        minRows={2}
        multiline
        onChange={(event) => onChange({ inclusions: event.target.value })}
        required
        size="small"
        value={category.inclusions}
      />

      <TextField
        disabled={!enabled}
        label="Bedding Type"
        onChange={(event) => onChange({ beddingType: event.target.value as EgsGuestCategoryDraft['beddingType'] })}
        required
        select
        size="small"
        value={category.beddingType}
      >
        {EGS_BEDDING_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {category.beddingType === 'other' && (
        <TextField
          disabled={!enabled}
          label="Other Extra Bedding Type"
          onChange={(event) => onChange({ otherBeddingType: event.target.value })}
          required
          size="small"
          value={category.otherBeddingType}
        />
      )}

      <TextField
        disabled={!enabled}
        label="Rate Type"
        onChange={(event) => onChange({ rateType: event.target.value as EgsGuestCategoryDraft['rateType'] })}
        required
        select
        size="small"
        value={category.rateType}
      >
        {EGS_RATE_TYPE_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <Typography color="text.secondary" variant="caption">
        {`Costs should be in ${currency}. If this Age Category is FOC, please add $0 as Cost and Sell.`}
      </Typography>

      <Stack direction="row" spacing={1}>
        <TextField
          disabled={!enabled}
          label="Cost"
          onChange={(event) => onChange({ costPrice: event.target.value })}
          required
          size="small"
          type="number"
          value={category.costPrice}
        />
        <TextField
          disabled={!enabled}
          label="Sell (AUD)"
          onChange={(event) => onChange({ sellPriceAud: event.target.value })}
          required
          size="small"
          type="number"
          value={category.sellPriceAud}
        />
      </Stack>
    </>
  );
}
